import { useTranslation } from 'react-i18next';
import React, { useEffect, useState } from 'react';
import { Button, Select, message, Divider, Modal, Tabs } from 'antd';
import { useQuery, useMutation } from '@apollo/client';

import { UNBIND_PRICING_SCHEME } from '../graphql/mutations/pricingSchemeApplyMutation';
import { GET_CLIENT_PRICING_SCHEME_APPLY_DETAIL } from '../graphql/queries/getClientPricingSchemeApplyDetail';
import { GET_PRICING_SCHEME_DETAIL, PricingSchemeDetailResponse } from 'graphql/queries/settlement/getPricingSchemeDetail';
import { ResourceCode } from 'constants/resourceCodes';
import { HasResource } from 'components/HasResource';
import { BUSINESS_SCENARIO_COST_PERIODIC, COST_PERIODIC_FEE_ITEM_CODE } from 'graphql/queries/settlement/getSettlementCommonQueries';
import { LIST_SCHEME_SELECT_OPTIONS } from '../../graphql/queries/listSchemeSelectOptions';
import { formatTierDiscountDisplay } from '../../periodic/CostPeriodicTierConfig';
import { resolvePricingSchemeMessage } from 'utils/graphqlUtils';
import { formatApplyTimeWithTimezone } from 'utils/timezoneFormatter';


interface Props {
  onSuccess?: () => void;
  onCancel?: () => void;
}

const CostPeriodicUnbindSchemeComp: React.FC<Props> = ({ onSuccess, onCancel }) => {
  const { t } = useTranslation();
  const [selectedSchemeId, setSelectedSchemeId] = useState<number | null>(null);
  const [selectedHubs, setSelectedHubs] = useState<string[]>([]);

  const { data: schemeOptions } = useQuery(LIST_SCHEME_SELECT_OPTIONS, {
    variables: { businessScenario: BUSINESS_SCENARIO_COST_PERIODIC, channelId: 0, isBind: true },
  });
  const { data: applyDetail } = useQuery(GET_CLIENT_PRICING_SCHEME_APPLY_DETAIL, {
    variables: { clientId: 0, pricingSchemeId: selectedSchemeId },
    skip: !selectedSchemeId,
    fetchPolicy: 'network-only',
  });
  const { data: detailData } = useQuery<PricingSchemeDetailResponse>(GET_PRICING_SCHEME_DETAIL, {
    variables: { pricingSchemeId: selectedSchemeId },
    skip: !selectedSchemeId,
    fetchPolicy: 'network-only',
  });

  const [unbindScheme, { loading }] = useMutation(UNBIND_PRICING_SCHEME);

  useEffect(() => {
    const applyDetailResult = applyDetail?.getClientPricingSchemeApplyDetail;
    if (applyDetailResult?.messageKey || applyDetailResult?.message) {
      message.error(resolvePricingSchemeMessage(applyDetailResult, '加载客户方案绑定详情失败'));
    }
    const detailResult = detailData?.getPricingSchemeDetail;
    if (detailResult?.messageKey || detailResult?.message) {
      message.error(resolvePricingSchemeMessage(detailResult, '加载定价方案详情失败'));
    }
  }, [applyDetail, detailData]);

  const getTiers = () => {
    const config = detailData?.getPricingSchemeDetail?.priceConfig?.find(
      p => Number(p.pricingFeeItemCode) === COST_PERIODIC_FEE_ITEM_CODE
    );
    if (!config?.allPriceConfig?.[0]?.contentPriceConfig) return [];
    try { return JSON.parse(config.allPriceConfig[0].contentPriceConfig); } catch { return []; }
  };

  const boundValues: string[] = applyDetail?.getClientPricingSchemeApplyDetail?.bindValues ?? [];

  const tiers = getTiers();

  const doUnbind = async () => {
    try {
      const { data, errors } = await unbindScheme({
        variables: {
          input: { clientId: 0, pricingSchemeId: selectedSchemeId!, unBindValue: selectedHubs },
        },
      });
      if (errors?.length) { message.error(errors[0].message); return; }
      if (data?.unbindPricingSchema?.success) {
        message.success(t('settlement.priceCenter.messages.unbindSuccess'));
        onSuccess?.();
      } else {
        message.error(resolvePricingSchemeMessage(data?.unbindPricingSchema, t('settlement.priceCenter.messages.unbindFailed')));
      }
    } catch (e: any) {
      message.error(e?.message || t('errors.INTERNAL_ERROR'));
      throw e;
    }
  };

  const handleUnbind = () => {
    if (!selectedSchemeId) { message.error('请选择方案'); return; }
    if (selectedHubs.length === 0) { message.error('请选择解绑维度'); return; }

    Modal.confirm({
      title: '确认解绑方案',
      icon: <span style={{ color: '#ff4d4f', fontSize: '22px' }}>⚠️</span>,
      content: (
        <div>
          <p style={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '16px', color: '#ff4d4f' }}>
            警告：此操作不可撤销！
          </p>
          <p style={{ marginBottom: '8px' }}>您即将解绑以下信息：</p>
          <div style={{ padding: '12px', background: '#fff7e6', border: '1px solid #ffd591', borderRadius: '4px', marginTop: '12px' }}>
            <p><strong>方案ID：</strong>{selectedSchemeId}</p>
            <p><strong>解绑维度：</strong>{selectedHubs.join('、')}</p>
          </div>
          <p style={{ marginTop: '16px', color: '#ff4d4f' }}>
            解绑后，该方案将不再对这些维度生效。
          </p>
        </div>
      ),
      okText: '确认解绑',
      okType: 'danger',
      cancelText: '取消',
      width: 520,
      onOk: doUnbind,
    });
  };

  return (
    <div>

      {/* 方案选择 + 备注 */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 12 }}>
        <span className="font-medium">选择方案</span>
        <Select
          style={{ width: 200 }}
          placeholder="请选择"
          options={schemeOptions?.allOptions?.map((o: { value: string; label: string }) => ({
            value: Number(o.value),
            label: o.label,
          }))}
          value={selectedSchemeId}
          onChange={v => { setSelectedSchemeId(v); setSelectedHubs([]); }}
        />
        <span className="font-medium ml-4">方案备注</span>
        <span style={{ color: '#555' }}>{detailData?.getPricingSchemeDetail?.pricingSchemeDescription ?? ''}</span>
      </div>

      {/* 方案详情 Tabs 展示 */}
      {tiers.length > 0 && (
        <Tabs size="middle" items={[{
          key: 'nthOrderSameAddrDiscount',
          label: '第N件N折',
          children: (
            <div>
              <div style={{ display: 'flex', fontWeight: 500, fontSize: 13, color: '#555', paddingBottom: 8, borderBottom: '1px solid #f0f0f0', marginBottom: 8 }}>
                <span style={{ width: 160, flexShrink: 0 }}>费用类型</span>
                <span style={{ flex: 1 }}>阶梯</span>
                <span style={{ width: 170, flexShrink: 0, textAlign: 'right' }}>折扣类型</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'flex-start' }}>
                <span style={{ width: 160, flexShrink: 0, paddingTop: 4 }}>DSP基础运费</span>
                <div style={{ flex: 1 }}>
                  {tiers.map((tier: any, i: number) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', marginBottom: 4 }}>
                      <span style={{ flex: 1, color: '#333' }}>{tier.startOrderCount} - {tier.endOrderCount} 单</span>
                      <span style={{ width: 170, textAlign: 'right', color: '#888' }}>{formatTierDiscountDisplay(tier)}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ),
        }]} style={{ marginBottom: 0 }} />
      )}

      {boundValues.length > 0 && (
        <>
          <Divider />
          <div className="mb-4" style={{ color: '#555' }}>
            <div style={{ marginBottom: 6 }}>
              <span style={{ fontWeight: 500 }}>当前已绑定维度：</span>
              <span>{boundValues.join('、')}</span>
            </div>
            <div>
              <span style={{ fontWeight: 500 }}>生效时间：</span>
              <span>
                {formatApplyTimeWithTimezone(
                  applyDetail?.getClientPricingSchemeApplyDetail?.effectiveTime,
                  applyDetail?.getClientPricingSchemeApplyDetail?.timezone
                )}
              </span>
            </div>
          </div>

          <div className="mb-4">
            <div className="font-medium mb-2">选择解绑维度：</div>
            <Select
              mode="multiple"
              style={{ width: 400 }}
              placeholder="请选择要解绑的 HUB"
              options={boundValues.map(v => ({ value: v, label: v }))}
              value={selectedHubs}
              onChange={setSelectedHubs}
            />
          </div>
        </>
      )}

      <div className="flex justify-end gap-4 mt-6">
        <HasResource resourceCodes={[ResourceCode.settlement.SETTLEMENT_COST_PERIODIC_PRICE_APPLY_OPERATE]}>
          <Button type="primary" onClick={handleUnbind} loading={loading} disabled={!selectedSchemeId}>
            确认解绑
          </Button>
        </HasResource>
        <Button onClick={onCancel}>返回</Button>
      </div>
    </div>
  );
};

export default CostPeriodicUnbindSchemeComp;
